/** Session-local successful reads. No credentials or records survive a Shell. */
export const DATA_CHANGED_EVENT = "console-data-changed";

export class ResourceCache {
  private entries = new Map<string, { data: unknown; at: number }>();
  private pending = new Map<string, { request: Promise<unknown>; revision?: number }>();
  private epoch = 0;
  private purgeEpoch = 0;
  private listeners = new Set<() => void>();

  constructor(private capacity = 32, private ttl = 5 * 60_000, private now = Date.now) {}

  get<T>(key: string): T | null {
    const entry = this.entries.get(key);
    if (!entry) return null;
    if (this.now() - entry.at >= this.ttl) {
      this.entries.delete(key);
      return null;
    }
    this.entries.delete(key);
    this.entries.set(key, entry);
    return entry.data as T;
  }

  /** Publish a successful mutation result and supersede older reads of this key. */
  set<T>(key: string, data: T) {
    this.pending.delete(key);
    this.entries.delete(key);
    this.entries.set(key, { data, at: this.now() });
    while (this.entries.size > this.capacity) this.entries.delete(this.entries.keys().next().value!);
  }

  /** Drop pending ownership too: an old response must not resurrect invalid data. */
  clear() {
    this.entries.clear();
    this.pending.clear();
  }

  /** A refresh keeps last-good snapshots; security/deletion invalidation drops them.
   * Neither extends entry timestamps, so signed media still expires on schedule. */
  invalidate = (purge = false) => {
    if (purge) {
      this.clear();
      this.purgeEpoch += 1;
    } else {
      this.pending.clear();
    }
    this.epoch += 1;
    for (const listener of this.listeners) listener();
  };
  version = () => this.epoch;
  purgeVersion = () => this.purgeEpoch;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  load<T>(key: string, fetcher: () => Promise<T>, revision?: number): Promise<T> {
    const pending = this.pending.get(key);
    if (pending && (revision === undefined || pending.revision === revision)) return pending.request as Promise<T>;
    const request = Promise.resolve().then(fetcher).then(data => {
      if (this.pending.get(key)?.request === request) {
        this.entries.delete(key);
        this.entries.set(key, { data, at: this.now() });
        while (this.entries.size > this.capacity) this.entries.delete(this.entries.keys().next().value!);
      }
      return data;
    }).finally(() => {
      if (this.pending.get(key)?.request === request) this.pending.delete(key);
    });
    this.pending.set(key, { request, revision });
    return request;
  }
}

/** Only use with an explicit endpoint parameter contract. */
export function resourceQuery(search: string, keys: readonly string[]): string {
  const input = new URLSearchParams(search);
  const output = new URLSearchParams();
  for (const key of [...keys].sort()) {
    for (const value of input.getAll(key)) output.append(key, value);
  }
  return output.size ? `?${output}` : "";
}

export const inventoryQuery = (search: string) => resourceQuery(search,
  ["workspace", "site", "groupBy", "stalledOnly", "sort", "severity"]);

/** Home and its shell share one read, without retaining anything outside the session cache. */
export const homeQuery = (search: string) => resourceQuery(search, ["workspace", "site", "project"]);
